import { RefObject, useEffect } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

// Give the container tabIndex={-1} so focus can land on the dialog itself, not the destructive action.
export const useModalA11y = (
  isOpen: boolean,
  containerRef: RefObject<HTMLElement | null>,
): void => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const container = containerRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const { body } = document;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    const previousOverflow = body.style.overflow;
    const previousPaddingRight = body.style.paddingRight;

    body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    container?.focus();

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !container) {
        return;
      }
      const items = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter(
        (item) =>
          item.tabIndex >= 0 &&
          !item.matches(":disabled") &&
          !item.closest('[hidden], [inert], [aria-hidden="true"]') &&
          item.getClientRects().length > 0 &&
          getComputedStyle(item).visibility !== "hidden",
      );
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (
        event.shiftKey &&
        (active === first ||
          active === container ||
          !container.contains(active))
      ) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (
        !event.shiftKey &&
        (active === last || active === container || !container.contains(active))
      ) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleTab, true);

    return () => {
      document.removeEventListener("keydown", handleTab, true);
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPaddingRight;
      previouslyFocused?.focus?.();
    };
  }, [isOpen, containerRef]);
};
